import {
  AnalysisStatus,
  ISearchItemFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { UnreachableCaseError } from 'ts-essentials';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SvgIcon } from '../../../../components/Icon';
import styled from 'styled-components';
import Avatar from '../../../../components/Avatar';
import { getClickRedirectUrl, getIconNameBySearchEntityType } from './utils';
import { PrimaryButton } from '../../../../components/Button';
import { globalFormat } from '../../../../utils/formatting';
import { theme } from '../../../../styles/theme';
import {
  optionHover,
  overflowingStyles,
} from '../../../../styles/shareableStyles';
import ReportStatusBadge from '../../../../components/ReportStatusBadge/ReportStatusBadge';

interface IProps {
  searchItem: ISearchItemFieldsFragment;
  onClick: () => void;
  showAddButton?: boolean;
  isLoading?: boolean;
}

const LeftContainer = styled.div`
  flex: 1;
  display: flex;
  column-gap: 8px;
`;
const Container = styled(Link)`
  flex: 0 0 auto;
  display: flex;
  column-gap: 8px;
  height: 38px;
  align-items: center;
  padding-right: 16px;
  padding-left: 16px;
  color: ${theme.colors.secondary800};
  &:hover {
    cursor: pointer;
    color: ${theme.colors.mainBlue};
    ${optionHover};
    ${LeftContainer} * {
      color: ${theme.colors.mainBlue};
    }
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 2px;
`;

const EntityName = styled.div`
  font-size: 12px;
  color: ${theme.colors.secondary900};
  ${overflowingStyles};
`;
const EntitySubtitle = styled.div`
  font-size: 9px;
  color: ${theme.colors.secondary600};
  ${overflowingStyles};
`;

const MIDDLE_DOT = '\u00B7';

export const generateSubtitle = (
  entity: ISearchItemFieldsFragment['entity'],
) => {
  const { dateFormat, timeFormat } = globalFormat.getGlobalFormat();
  switch (entity.__typename) {
    case 'IdSearchEntity':
      const disabledTextWithDot = entity.isDisabled
        ? `${MIDDLE_DOT} Disabled ${MIDDLE_DOT}`
        : `${MIDDLE_DOT}`;
      if (!entity.nullableSchema) {
        return `
            ${globalFormat.formatDate(entity.createdAt, {
              dateFormat,
              timeFormat,
            })} ${disabledTextWithDot} ${entity.editor.name}
`;
      } else {
        return (
          `${entity.nullableSchema.name} ${MIDDLE_DOT} ` +
          `${globalFormat.formatDate(entity.createdAt, {
            dateFormat,
            timeFormat,
          })} ${disabledTextWithDot} ${entity.editor.name}`
        );
      }
    case 'KeySearchEntity':
      return `${entity.schema.name}${
        entity.isDisabled ? `${MIDDLE_DOT} Disabled` : ''
      }`;
    case 'SearchDashboard':
      return `${globalFormat.formatDate(entity.createdAt, {
        dateFormat,
        timeFormat,
      })} ${MIDDLE_DOT} ${entity.path} ${MIDDLE_DOT} ${entity.creator.name}`;
    case 'SearchAnalysis':
      const status =
        entity.status !== AnalysisStatus.Success ? (
          <ReportStatusBadge status={entity.status} />
        ) : null;
      return (
        <>
          {status}{' '}
          {globalFormat.formatDate(entity.createdAt, {
            dateFormat,
            timeFormat,
          })}{' '}
          {MIDDLE_DOT} {entity.editor.name}
        </>
      );
    case 'SearchSchedule':
      const disabledText = entity.isDisabled
        ? `${MIDDLE_DOT} Disabled ${MIDDLE_DOT}`
        : `${MIDDLE_DOT}`;
      const basicInfo = `${globalFormat.formatDate(entity.createdAt, {
        dateFormat,
        timeFormat,
      })} ${disabledText} ${entity.editor.name}`;
      if (entity.nullableSchema) {
        return `${entity.nullableSchema.name} ${MIDDLE_DOT} ${basicInfo}`;
      }
      return basicInfo;
    case 'SearchDashboardFolder':
      return '';
    default:
      throw new UnreachableCaseError(entity);
  }
};

const SearchResultItem = ({
  searchItem,
  onClick,
  showAddButton,
  isLoading,
}: IProps) => {
  const [onMouseOver, setOnMouseOver] = useState(false);

  return (
    <Container
      to={getClickRedirectUrl(searchItem.entity)}
      onMouseOver={() => setOnMouseOver(true)}
      onMouseLeave={() => setOnMouseOver(false)}
      onClick={(event) => {
        event.preventDefault();
        onClick();
      }}
    >
      <LeftContainer>
        {/* some icons use fill-rule="nonzero" some are fill="currentColor" */}
        {/* therefore to display correct color, we need to both assign isActive on fill for non-currentColor svg */}
        {/* and also parent color on currentColor svg */}
        <SvgIcon
          name={getIconNameBySearchEntityType(searchItem.entity)}
          width={16}
          height={16}
          isActive={onMouseOver}
        />
        <ContentContainer>
          <EntityName>{searchItem.entity.name}</EntityName>
          <EntitySubtitle>{generateSubtitle(searchItem.entity)}</EntitySubtitle>
        </ContentContainer>
      </LeftContainer>
      {showAddButton && onMouseOver && (
        <PrimaryButton
          trackContext="search"
          trackAction="add"
          disabled={isLoading}
          loading={isLoading}
        >
          + Add
        </PrimaryButton>
      )}
      {searchItem.entity.__typename !== 'KeySearchEntity' && (
        <Avatar picture={searchItem.entity.editor.picture} size={24} />
      )}
    </Container>
  );
};
export default SearchResultItem;
